import { useState, useEffect } from "react";
import { Copy, Check, EnvelopeSimple, Clock, PaperPlaneTilt, SpinnerGap, Warning } from "@phosphor-icons/react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EmailPreview({ emails, prospectId, prospectEmail }) {
  const [copiedId, setCopiedId] = useState(null);
  const [sendingId, setSendingId] = useState(null);
  const [sendResult, setSendResult] = useState({});
  const [emailConfig, setEmailConfig] = useState(null);

  useEffect(() => {
    axios.get(`${API}/settings/email`).then(res => setEmailConfig(res.data)).catch(() => {});
  }, []);

  const handleCopy = (email) => {
    const text = `Subject: ${email.subject}\n\n${email.body}`;
    navigator.clipboard.writeText(text);
    setCopiedId(email.id);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const handleSend = async (email) => {
    if (!prospectEmail && !email._overrideEmail) return;
    setSendingId(email.id);
    try {
      const res = await axios.post(`${API}/send-email`, {
        prospect_id: prospectId,
        email_id: email.id,
        to_email: email._overrideEmail || prospectEmail || null,
      });
      setSendResult({ ...sendResult, [email.id]: { sent: true, to: res.data.to_email } });
    } catch (e) {
      const detail = e.response?.data?.detail || "Send failed";
      setSendResult({ ...sendResult, [email.id]: { sent: false, error: detail } });
    } finally {
      setSendingId(null);
    }
  };

  return (
    <div data-testid="email-preview-section">
      <h3
        className="text-lg font-bold tracking-tight mb-4"
        style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}
      >
        Generated Emails
        <span className="ml-2 text-sm font-normal text-[--text-muted]">({emails.length})</span>
      </h3>

      <div className="space-y-4">
        {emails.map((email) => (
          <div
            key={email.id}
            className="bg-white border-2 border-[--primary] relative overflow-hidden email-glow"
            data-testid={`email-card-${email.id}`}
          >
            <div className="h-1 bg-gradient-to-r from-[--primary] via-indigo-500 to-[--primary]" />

            <div className="p-6">
              {/* Meta */}
              <div className="flex items-center justify-between mb-4">
                <div className="flex items-center gap-2">
                  <EnvelopeSimple size={16} className="text-[--primary]" weight="duotone" />
                  <span className="text-[10px] font-bold uppercase tracking-wider text-[--primary]">
                    AI Generated · {email.tone}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-[--text-muted] flex items-center gap-1">
                    <Clock size={10} />
                    {new Date(email.created_at).toLocaleDateString()}
                  </span>
                  <button
                    data-testid={`copy-email-${email.id}`}
                    onClick={() => handleCopy(email)}
                    className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium border border-[--border] hover:bg-[--bg-muted] transition-all active:scale-95"
                  >
                    {copiedId === email.id ? (
                      <><Check size={12} className="text-[--success]" weight="bold" /> Copied!</>
                    ) : (
                      <><Copy size={12} /> Copy</>
                    )}
                  </button>

                  {/* Send button */}
                  {emailConfig?.configured && (prospectEmail || false) && (
                    <button
                      data-testid={`send-email-${email.id}`}
                      onClick={() => handleSend(email)}
                      disabled={sendingId === email.id || sendResult[email.id]?.sent}
                      className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium bg-[--primary] text-white hover:bg-[--primary-hover] transition-all active:scale-95 disabled:opacity-50"
                    >
                      {sendingId === email.id ? (
                        <><SpinnerGap size={12} className="animate-spin" /> Sending...</>
                      ) : sendResult[email.id]?.sent ? (
                        <><Check size={12} weight="bold" /> Sent</>
                      ) : (
                        <><PaperPlaneTilt size={12} weight="bold" /> Send</>
                      )}
                    </button>
                  )}
                </div>
              </div>

              {/* Send result message */}
              {sendResult[email.id] && (
                <div className={`mb-3 p-2 text-xs ${
                  sendResult[email.id].sent
                    ? "bg-[--success-bg] text-[#008A39]"
                    : "bg-[--danger-bg] text-[--danger]"
                }`}>
                  {sendResult[email.id].sent
                    ? `Sent to ${sendResult[email.id].to}`
                    : sendResult[email.id].error
                  }
                </div>
              )}

              {/* Subject */}
              <div className="mb-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[--text-muted]">Subject</span>
                <p className="text-sm font-semibold text-[--text-primary] mt-1">{email.subject}</p>
              </div>

              {/* Body */}
              <div className="border-t border-[--border] pt-3">
                <span className="text-[10px] font-bold uppercase tracking-wider text-[--text-muted]">Body</span>
                <div
                  className="mt-2 text-sm text-[--text-secondary] leading-relaxed whitespace-pre-wrap"
                  style={{ fontFamily: "'IBM Plex Sans', sans-serif" }}
                >
                  {email.body}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* SendGrid not configured notice */}
      {emailConfig && !emailConfig.configured && (
        <div className="mt-4 p-3 bg-[--warning-bg] border border-[--warning] text-xs text-[#B38F00] flex items-start gap-2" data-testid="sendgrid-notice">
          <Warning size={14} className="flex-shrink-0 mt-0.5" weight="bold" />
          <div>
            <strong>Email sending not configured.</strong> To send emails directly, add your SendGrid API key and sender email to the backend .env file.
            You can still copy emails and send them manually.
          </div>
        </div>
      )}
    </div>
  );
}
